import React, { useState, useEffect } from 'react';
import { 
    Star, 
    Users, 
    Plus, 
    Edit3, 
    Trash2, 
    Search,
    Loader,
    CheckCircle,
    AlertCircle,
    ArrowUp,
    ArrowDown,
    User,
    Music
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const HighlightedArtistsAdmin = () => {
    const { user } = useAuth();
    const [highlightedArtists, setHighlightedArtists] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [usersLoading, setUsersLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingArtist, setEditingArtist] = useState(null);

    useEffect(() => {
        fetchHighlightedArtists();
        fetchAllUsers();
    }, []);

    const fetchHighlightedArtists = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/highlighted-artists');
            
            if (response.data.success) {
                setHighlightedArtists(response.data.data);
            } else {
                setError('Failed to load highlighted artists');
            }
        } catch (err) {
            console.error('Error fetching highlighted artists:', err);
            setError('Unable to load highlighted artists');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllUsers = async () => {
        try {
            setUsersLoading(true);
            const response = await api.get('/admin/crew/users?limit=100');
            
            if (response.data.success) {
                // Filter out users who are already highlighted
                const availableUsers = response.data.data.filter(u => 
                    !highlightedArtists.find(ha => ha.user_id === u.user_id)
                );
                setAllUsers(availableUsers);
            }
        } catch (err) {
            console.error('Error fetching users:', err);
        } finally {
            setUsersLoading(false);
        }
    };

    const addToHighlighted = async (userId, description, order) => {
        try {
            const response = await api.post(`/admin/highlighted-artists/${userId}`, {
                description: description.trim(),
                order: parseInt(order) || 0
            });
            
            if (response.data.success) {
                setSuccess(response.data.message);
                setShowAddModal(false);
                await fetchHighlightedArtists();
                await fetchAllUsers();
            } else {
                setError(response.data.message);
            }
        } catch (err) {
            console.error('Error adding to highlighted:', err);
            setError(err.response?.data?.message || 'Failed to add user to highlighted artists');
        }
    };

    const updateHighlighted = async (userId, description, order) => {
        try {
            const response = await api.put(`/admin/highlighted-artists/${userId}`, {
                description: description.trim(),
                order: parseInt(order) || 0
            });
            
            if (response.data.success) {
                setSuccess(response.data.message);
                setEditingArtist(null);
                await fetchHighlightedArtists();
            } else {
                setError(response.data.message);
            }
        } catch (err) {
            console.error('Error updating highlighted artist:', err);
            setError(err.response?.data?.message || 'Failed to update highlighted artist');
        }
    };

    const removeFromHighlighted = async (userId, userName) => {
        if (!confirm(`Are you sure you want to remove ${userName} from highlighted artists?`)) {
            return;
        }

        try {
            const response = await api.delete(`/admin/highlighted-artists/${userId}`);
            
            if (response.data.success) {
                setSuccess(response.data.message);
                await fetchHighlightedArtists();
                await fetchAllUsers();
            } else {
                setError(response.data.message);
            }
        } catch (err) {
            console.error('Error removing from highlighted:', err);
            setError(err.response?.data?.message || 'Failed to remove user from highlighted artists');
        }
    };

    const clearMessages = () => {
        setError(null);
        setSuccess(null);
    };

    const filteredUsers = allUsers.filter(user =>
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const AddHighlightedModal = () => {
        const [selectedUser, setSelectedUser] = useState('');
        const [description, setDescription] = useState('');
        const [order, setOrder] = useState(0);

        const handleSubmit = (e) => {
            e.preventDefault();
            if (selectedUser) {
                addToHighlighted(selectedUser, description, order);
            }
        };

        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-md">
                    <div className="flex items-center justify-between p-6 border-b border-gray-700">
                        <h3 className="text-xl font-bold text-white">Add Highlighted Artist</h3>
                        <button
                            onClick={() => setShowAddModal(false)}
                            className="text-gray-400 hover:text-white"
                        >
                            ✕
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Select User
                            </label>
                            <select
                                value={selectedUser}
                                onChange={(e) => setSelectedUser(e.target.value)}
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                                required
                            >
                                <option value="">Choose a user...</option>
                                {filteredUsers.map((u) => (
                                    <option key={u.user_id} value={u.user_id}>
                                        {u.display_name || u.username} ({u.email})
                                        {u.is_artist && ' - Artist'}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Description (Optional)
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Special highlight description for this artist..."
                                rows={3}
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Display Order
                            </label>
                            <input
                                type="number"
                                value={order}
                                onChange={(e) => setOrder(e.target.value)}
                                placeholder="0"
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                            />
                            <p className="text-xs text-gray-400 mt-1">Lower numbers appear first</p>
                        </div>

                        <div className="flex gap-3 pt-4">
                            <button
                                type="submit"
                                className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white py-2 px-4 rounded-lg transition-colors"
                                disabled={!selectedUser}
                            >
                                Add to Highlighted
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowAddModal(false)}
                                className="px-4 py-2 text-gray-400 hover:text-white"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    const EditHighlightedModal = ({ artist }) => {
        const [description, setDescription] = useState(artist.highlighted_artist_description || '');
        const [order, setOrder] = useState(artist.highlighted_artist_order || 0);

        const handleSubmit = (e) => {
            e.preventDefault();
            updateHighlighted(artist.user_id, description, order);
        };

        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-md">
                    <div className="flex items-center justify-between p-6 border-b border-gray-700">
                        <h3 className="text-xl font-bold text-white">
                            Edit {artist.display_name || artist.username}
                        </h3>
                        <button
                            onClick={() => setEditingArtist(null)}
                            className="text-gray-400 hover:text-white"
                        >
                            ✕
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Description
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Special highlight description for this artist..."
                                rows={3}
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Display Order
                            </label>
                            <input
                                type="number"
                                value={order}
                                onChange={(e) => setOrder(e.target.value)}
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                            />
                            <p className="text-xs text-gray-400 mt-1">Lower numbers appear first</p>
                        </div>

                        <div className="flex gap-3 pt-4">
                            <button
                                type="submit"
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
                            >
                                Update
                            </button>
                            <button
                                type="button"
                                onClick={() => setEditingArtist(null)}
                                className="px-4 py-2 text-gray-400 hover:text-white"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-7xl mx-auto px-6">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <Star className="w-8 h-8 text-yellow-400" />
                        <h1 className="text-4xl font-black gradient-text">Highlighted Artists Management</h1>
                    </div>
                    <p className="text-gray-400">
                        Manage featured artists displayed on the homepage and dashboard
                    </p>
                </div>

                {/* Error/Success Messages */}
                {error && (
                    <div className="glass-card p-4 mb-6 border border-red-500/30 bg-red-500/10">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <AlertCircle className="w-5 h-5 text-red-400" />
                                <span className="text-red-400">{error}</span>
                            </div>
                            <button onClick={clearMessages} className="text-red-400 hover:text-red-300">✕</button>
                        </div>
                    </div>
                )}

                {success && (
                    <div className="glass-card p-4 mb-6 border border-green-500/30 bg-green-500/10">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <CheckCircle className="w-5 h-5 text-green-400" />
                                <span className="text-green-400">{success}</span>
                            </div>
                            <button onClick={clearMessages} className="text-green-400 hover:text-green-300">✕</button>
                        </div>
                    </div>
                )}

                {/* Actions Bar */}
                <div className="glass-card p-4 mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search users..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 w-64"
                                />
                            </div>
                        </div>
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="btn-primary flex items-center gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            Add Highlighted Artist
                        </button>
                    </div>
                </div>

                {/* Current Highlighted Artists */}
                <div className="glass-card p-6">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                        <Star className="w-5 h-5 text-yellow-400" />
                        Current Highlighted Artists ({highlightedArtists.length})
                    </h2>

                    {loading ? (
                        <div className="text-center py-8">
                            <Loader className="w-8 h-8 animate-spin text-yellow-500 mx-auto mb-3" />
                            <p className="text-gray-400">Loading highlighted artists...</p>
                        </div>
                    ) : highlightedArtists.length === 0 ? (
                        <div className="text-center py-8">
                            <Star className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                            <p className="text-gray-400">No highlighted artists yet</p>
                            <p className="text-gray-500 text-sm">Add some artists to get started!</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {highlightedArtists.map((artist) => (
                                <div key={artist.id} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
                                    <div className="flex items-center gap-4">
                                        <div className="relative">
                                            {artist.profile_picture_url ? (
                                                <img
                                                    src={artist.profile_picture_url}
                                                    alt={artist.display_name || artist.username}
                                                    width="48"
                                                    height="48"
                                                    className="w-12 h-12 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center">
                                                    <span className="text-2xl leading-none">🌊</span>
                                                </div>
                                            )}
                                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                                                <Star className="w-2 h-2 text-yellow-900 fill-current" />
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-semibold text-white">
                                                    {artist.display_name || artist.username}
                                                </h3>
                                                {artist.is_artist && (
                                                    <Music className="w-4 h-4 text-purple-400" />
                                                )}
                                                {artist.artist_verified && (
                                                    <CheckCircle className="w-4 h-4 text-blue-400" />
                                                )}
                                            </div>
                                            <p className="text-gray-400 text-sm">{artist.email}</p>
                                            {artist.highlighted_artist_description && (
                                                <p className="text-gray-300 text-sm mt-1 max-w-md">
                                                    {artist.highlighted_artist_description}
                                                </p>
                                            )}
                                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                                <span>Order: {artist.highlighted_artist_order}</span>
                                                <span>•</span>
                                                <span>
                                                    Added: {new Date(artist.highlighted_added_date).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setEditingArtist(artist)}
                                            className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
                                            title="Edit"
                                        >
                                            <Edit3 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => removeFromHighlighted(artist.user_id, artist.display_name || artist.username)}
                                            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                                            title="Remove"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Modals */}
                {showAddModal && <AddHighlightedModal />}
                {editingArtist && <EditHighlightedModal artist={editingArtist} />}
            </div>
        </div>
    );
};

export default HighlightedArtistsAdmin;